import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getBackendBaseUrl } from '../utils/backendUrl';
import { useAuth } from '../contexts/AuthContext';
import { hasNotificationPermission, showBrowserNotification } from '../utils/browserNotifications';
import {
  getNotificationId,
  isSellerRelevantNotification,
  SellerNotification,
} from '../types/notifications';
import {
  invalidateSellerLiveData,
  notificationTypeToInvalidateScope,
} from '../lib/invalidateSellerLive';

/** SSE requires backend token-in-query support — off by default. */
const SSE_ENABLED =
  process.env.REACT_APP_ENABLE_NOTIFICATION_SSE === 'true' ||
  process.env.REACT_APP_ENABLE_NOTIFICATION_SSE === '1';

/**
 * Optional SSE subscription. Enable when backend supports authenticated stream.
 */
export function useNotificationStream() {
  const queryClient = useQueryClient();
  const { token, user } = useAuth();
  const isAuthenticated = !!token && !!user;
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!SSE_ENABLED || !isAuthenticated || !token) {
      return undefined;
    }

    const url = `${getBackendBaseUrl()}/api/v1/notifications/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    const onNotification = (event: MessageEvent) => {
      try {
        const notification = JSON.parse(event.data) as SellerNotification;
        const id = getNotificationId(notification);
        if (!id || !isSellerRelevantNotification(notification)) return;

        queryClient.setQueryData(['notifications'], (old: SellerNotification[] = []) => {
          if (old.some((n) => getNotificationId(n) === id)) return old;
          return [notification, ...old];
        });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-summary'] });

        if (hasNotificationPermission() && !document.hasFocus()) {
          showBrowserNotification(notification.title, {
            body: notification.message || notification.body,
            icon: '/logo192.png',
            tag: `notification-${id}`,
            link: notification.link || notification.actionUrl || '/orders',
            data: notification.payload || notification.data,
          });
        }

        const scope = notificationTypeToInvalidateScope(notification.type);
        if (scope) {
          void invalidateSellerLiveData(queryClient, scope);
        }
      } catch (e) {
        console.warn('SSE notification parse error', e);
      }
    };

    es.addEventListener('notification', onNotification);
    es.onerror = () => {
      es.close();
      esRef.current = null;
    };

    return () => {
      es.removeEventListener('notification', onNotification);
      es.close();
      esRef.current = null;
    };
  }, [isAuthenticated, token, queryClient]);
}

export default useNotificationStream;
